import React, { useEffect, useState } from 'react';
import DocumentNumberField from '../components/DocumentNumberField';
import FormActions from './FormActions';
import { Input, Select, FieldSet, Button } from '../../../components/ui';
import {
  SC_STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  SERVICE_REQUEST_DESCRIPTION_OPTIONS,
} from '../QDFConstants';

const ServiceRequestForm = ({
  formData,
  handleChange,
  handleSubmit,
  loading,
  generatingNumber,
  onCancel,
  isEditing = false,
  isPublicMode = false,
  shopConfig = null,
}) => {
  const groupedAssetTags = shopConfig?.groupedAssetTags || [];
  const assetNumbers = shopConfig?.assetNumbers || [];
  const locations = shopConfig?.locations || [];
  const departments = shopConfig?.departments || [];

  const [isCustomDescription, setIsCustomDescription] = useState(
    Boolean(formData.scDescription) &&
      !SERVICE_REQUEST_DESCRIPTION_OPTIONS.includes(formData.scDescription)
  );

  useEffect(() => {
    const isPredefined = SERVICE_REQUEST_DESCRIPTION_OPTIONS.includes(formData.scDescription);
    if (isPredefined) {
      setIsCustomDescription(false);
      return;
    }

    if (formData.scDescription) {
      setIsCustomDescription(true);
    }
  }, [formData.scDescription]);

  const isPredefinedDescription = SERVICE_REQUEST_DESCRIPTION_OPTIONS.includes(
    formData.scDescription
  );
  const descriptionSelection = isCustomDescription
    ? 'Other'
    : isPredefinedDescription
      ? formData.scDescription
      : '';

  const handleDescriptionSelectionChange = (e) => {
    const selectedValue = e.target.value;

    if (!selectedValue) {
      setIsCustomDescription(false);
      handleChange({ target: { name: 'scDescription', value: '' } });
      return;
    }

    if (selectedValue === 'Other') {
      setIsCustomDescription(true);
      if (isPredefinedDescription) {
        handleChange({ target: { name: 'scDescription', value: '' } });
      }
      return;
    }

    setIsCustomDescription(false);
    handleChange({ target: { name: 'scDescription', value: selectedValue } });
  };

  const autoFilled = 'Filled automatically from the selected asset tag.';

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <FieldSet legend="Service request details">
        <div className="grid gap-4 md:grid-cols-2">
          <DocumentNumberField type="SR" formData={formData} generatingNumber={generatingNumber} />

          {!isPublicMode && (
            <Select
              id="scStatus"
              name="scStatus"
              label="SR status"
              value={formData.scStatus}
              onChange={handleChange}
            >
              <option value="">Select status</option>
              {SC_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>
          )}

          <Input
            id="creationDate"
            name="creationDate"
            type="datetime-local"
            label="Creation date & time"
            value={formData.creationDate}
            onChange={handleChange}
          />

          {!isPublicMode && (
            <Input
              id="contractor"
              name="contractor"
              label="Contractor"
              value={formData.contractor}
              onChange={handleChange}
            />
          )}

          <Select
            id="priority"
            name="priority"
            label="Priority"
            value={formData.priority}
            onChange={handleChange}
            className="md:col-span-2"
          >
            <option value="">Select priority</option>
            {PRIORITY_OPTIONS.map((priority) => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </Select>

          <div className="md:col-span-2">
            <Select
              id="scDescriptionOption"
              name="scDescriptionOption"
              label="Description"
              value={descriptionSelection}
              onChange={handleDescriptionSelectionChange}
            >
              <option value="">Select description</option>
              {SERVICE_REQUEST_DESCRIPTION_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
              <option value="Other">Other (enter custom)</option>
            </Select>

            {descriptionSelection === 'Other' && (
              <Input
                id="scDescription"
                name="scDescription"
                label="Custom description"
                value={formData.scDescription}
                onChange={handleChange}
                placeholder="Describe the issue"
                className="mt-3"
              />
            )}
          </div>
        </div>
      </FieldSet>

      <FieldSet legend="Asset details">
        <div className="grid gap-4 md:grid-cols-2">
          <Select
            id="assetTag"
            name="assetTag"
            label="Asset tag"
            value={formData.assetTag}
            onChange={handleChange}
            disabled={isPublicMode}
          >
            <option value="">Select asset tag</option>
            {groupedAssetTags.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </optgroup>
            ))}
          </Select>

          <Select
            id="assetNumber"
            name="assetNumber"
            label="Asset number"
            value={formData.assetNumber}
            onChange={handleChange}
            disabled
            hint={autoFilled}
          >
            <option value="">Select asset number</option>
            {assetNumbers.map((num) => (
              <option key={num} value={num}>{num}</option>
            ))}
          </Select>

          <Select
            id="location"
            name="location"
            label="Location"
            value={formData.location}
            onChange={handleChange}
            disabled
            hint={autoFilled}
          >
            <option value="">Select location</option>
            {locations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </Select>

          <Select
            id="department"
            name="department"
            label="Department"
            value={formData.department}
            onChange={handleChange}
            disabled
            hint={autoFilled}
          >
            <option value="">Select department</option>
            {departments.map((dept) => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </Select>
        </div>
      </FieldSet>

      <FieldSet legend="Requester details">
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            id="requesterName"
            name="requesterName"
            label="Requester name"
            value={formData.requesterName}
            onChange={handleChange}
            autoComplete="name"
          />

          <Input
            id="requesterContact"
            name="requesterContact"
            label="Requester contact"
            value={formData.requesterContact}
            onChange={handleChange}
            placeholder="Phone or email"
            inputMode="tel"
          />
        </div>
      </FieldSet>

      {isPublicMode ? (
        // The public form has no page to cancel back to, so it gets a single
        // full-width submit rather than the cancel/submit pair.
        <div className="z-10 -mx-4 border-t border-border bg-surface/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] backdrop-blur sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none">
          <Button type="submit" size="lg" fullWidth loading={loading}>
            {loading ? 'Submitting…' : 'Submit service request'}
          </Button>
        </div>
      ) : (
        <FormActions
          onCancel={onCancel}
          loading={loading}
          isEditing={isEditing}
          submitLabel={loading ? (isEditing ? 'Updating…' : 'Saving…') : isEditing ? 'Update' : 'Save'}
        />
      )}
    </form>
  );
};

export default ServiceRequestForm;
